import React, { useRef, useState, useEffect } from "react";

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const BeforeAfterSlider = ({ backgroundImageUrl, overlayImageUrl, opened, className }) => {
  const containerRef = useRef(null);
  const [sliderX, setSliderX] = useState(50); // Start at 50%
  const [isDragging, setIsDragging] = useState(false);
  const [initialRevealDone, setInitialRevealDone] = useState(false);

  const startDragging = () => setIsDragging(true);
  const stopDragging = () => setIsDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = 100 - (x / rect.width) * 100;
    setSliderX(Math.max(0, Math.min(100, percentage)));
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const percentage = 100 - (x / rect.width) * 100;
    setSliderX(Math.max(0, Math.min(100, percentage)));
  };

  useEffect(() => {
    const preventScroll = (e) => isDragging && e.cancelable && e.preventDefault();

    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", preventScroll, { passive: false });
    return () => {
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", preventScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  useEffect(() => {
    if (!opened || initialRevealDone) {
      return;
    }

    let start = null;
    const duration = 2500;
    const from = 0;    // overlay fully visible
    const to = 100;    // overlay fully hidden

    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      // const eased = progress * (2 - progress); // easeOutQuad
      const eased = 0.5 * (1 - Math.cos(Math.PI * progress)); // easeInOutSine
      // const eased = progress < 0.5 ? 4 * progress**3 : 1 - Math.pow(-2 * progress + 2, 3) / 2; // ease-in-out (cubic)

      const current = from + (to - from) * eased;
      setSliderX(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    setInitialRevealDone(true);
    requestAnimationFrame(animate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  return (
    <div
      className={"u-rounded-025 absolute u-left-0 u-top-0 u-w-full u-h-full overflow-hidden bg-cover bg-center bg-no-repeat "
        + (className || "")}
      ref={containerRef}
      style={{ backgroundImage: `url(${backgroundImageUrl})` }}>
      <div className="absolute u-right-0 u-top-0 u-h-full u-w-full overflow-hidden before-after-overlay noselect"
           style={{'--clip-width': `${sliderX}%`}}>
        <div className="overlay-image-wrapper absolute u-h-full u-w-full u-top-0 u-left-0">
          <img className="u-h-full u-w-full block" src={overlayImageUrl} alt="After design"/>
        </div>
      </div>
      <div className="before-after-slider absolute u-top-0 u-h-full bg-black"
           style={{right: `${sliderX}%`}}
           onMouseDown={startDragging}
           onTouchStart={startDragging}
           onTouchEnd={stopDragging}
           onTouchMove={handleTouchMove} >
        <div className="handle absolute u-top-1/2 u-left-1/2 u-h-25 u-w-25 bg-white u-rounded-1/2 flex flex--center">
          <span className="handle-left u-w-15 u-h-15"><ChevronLeftIcon /></span>
          <span className="handle-right u-w-15 u-h-15"><ChevronRightIcon /></span>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
